import {
  Skeleton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
} from '@mui/material';

import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { colors } from '@client/new/components/colors';
import { usDollarFormatter } from '@client/new/helpers/currency-formatter';
import { Typography } from '@design-system/components/typography/typography';

import { HeaderTableCell } from './components/HeaderTableCell';
import { PaymentStatus } from './components/PaymentStatus';
import { OtpEarningsBreakdown, useOtpPayouts } from './useOtpPayouts';
import { usePaymentMethod } from './usePaymentMethod';

type RevenueTableRowProps = {
  name: string;
  unitsSold?: number;
  revenue?: number;
  renderIfZero?: boolean;
  sx?: SxProps<Theme>;
};

const RevenueTableRow: React.FC<RevenueTableRowProps> = ({
  name,
  unitsSold,
  revenue,
  renderIfZero = false,
  sx,
}) => {
  if (revenue === undefined) {
    return null;
  }

  if (!renderIfZero && revenue === 0) {
    return null;
  }

  return (
    <TableRow sx={sx}>
      <TableCell align="left" className="p-4">
        <Typography className="font-normal">{name}</Typography>
      </TableCell>
      <TableCell align="right" className="p-4">
        <Typography className="font-normal">{unitsSold}</Typography>
      </TableCell>
      <TableCell align="right" className="p-4">
        <Typography className="font-normal">
          {usDollarFormatter.format(revenue)}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

const OtpRevenueSection: React.FC<OtpEarningsBreakdown> = (props) => {
  const paymentMethod = usePaymentMethod();

  if (!props.hasPrograms) {
    return null;
  }

  return (
    <div>
      <div className="flex gap-2 mb-5 items-center">
        <Typography variant="headline" size="small">
          One-time Purchases Revenue
        </Typography>
        <PaymentStatus paidAt={props.paidAt} />
      </div>
      <div className="flex flex-col gap-[30px]"></div>
      <Table
        sx={{
          minWidth: 650,
          boxShadow:
            '0px 1px 2px 0px rgba(29, 41, 57, 0.08), 0px 0px 1px 0px #EAECF0',
          borderCollapse: 'collapse',
          borderRadius: '10px',
        }}
      >
        <TableHead
          sx={{
            backgroundColor: colors.secondary200,
          }}
        >
          <TableRow>
            <HeaderTableCell title="Program Name" align="left" />
            <HeaderTableCell title="Units Sold" align="right" />
            <HeaderTableCell title="Net Revenue" align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {props.programs.map((program, index) => (
            <RevenueTableRow
              key={index}
              name={program.name}
              unitsSold={program.unitsSold}
              revenue={program.revenue}
            />
          ))}
          <RevenueTableRow
            name="Refunds & Disputes"
            unitsSold={props.refundsAndDisputes.unitsSold}
            revenue={props.refundsAndDisputes.revenue}
            renderIfZero
          />
          <RevenueTableRow
            sx={{ borderTop: '2px solid' }}
            name="Total One-time Purchases Revenue"
            revenue={props.totalOtpProgramsRevenue}
            renderIfZero
          />
          <RevenueTableRow
            name={`${paymentMethod.name} Transfer Fee`}
            revenue={props.payoutFee}
            renderIfZero
          />
          <RevenueTableRow
            sx={{ borderTop: '2px solid' }}
            name="Total Monthly Payout"
            revenue={props.totalPayout}
            renderIfZero
          />
          {props.totalPayout === 0 && (
            <TableRow>
              <TableCell align="left" className="p-4 text-red-400" colSpan={3}>
                <Typography className="font-normal">
                  Payouts are processed for earnings of{' '}
                  {usDollarFormatter.format(10)} or more.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export type OtpRevenueProps = {
  creatorId: number;
  date: Date;
};

export const OtpRevenue = (props: OtpRevenueProps) => {
  const payouts = useOtpPayouts(props);

  return (
    <div>
      <AsyncRenderer
        data={payouts}
        loadRender={
          <>
            <Skeleton
              variant="rectangular"
              sx={{ width: '100%', height: 30, marginBottom: '10px' }}
            />
            <Skeleton
              variant="rectangular"
              sx={{ width: '100%', height: 30 }}
            />
          </>
        }
        renderer={(data) => <OtpRevenueSection {...data} />}
      />
    </div>
  );
};
